import React, { useEffect, useState } from 'react';
import { DollarSign, Loader2, Check } from 'lucide-react';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';

// What this visit was billed, and to which coverage. Pre-filled from the
// visit type's usual charge and the client's case funding path, so for the
// front desk it is usually one click. Saving moves the case's coverage
// "used" figure so the law firm's PIP-remaining stays true.
const KIND_LABEL = { pip: 'PIP', medpay: 'MedPay', health: 'Health insurance', lop: 'LOP', um_uim: 'UM/UIM', self_pay: 'Self-pay' };
const inputClass = 'px-3 py-2 rounded-[10px] bg-tertiary border border-theme text-[13px] text-primary-c focus:outline-none focus:border-strong transition-all';
const money = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function VisitBilling({ appointment, defaultCharge, onSaved }) {
  const { currentOrg } = useOrg();
  const raw = appointment._raw || {};
  const clientId = raw.client_id;
  const [caseRow, setCaseRow] = useState(null);
  const [loading, setLoading] = useState(!!clientId);
  const [amount, setAmount] = useState(raw.billed_amount != null ? String(raw.billed_amount) : (defaultCharge != null ? String(defaultCharge) : ''));
  const [kind, setKind] = useState(raw.billed_to || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(!!raw.billed_at);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentOrg?.id || !clientId) return;
    api.listCases(currentOrg.id)
      .then((list) => {
        const open = (list || []).filter((c) => c.client_id === clientId && !['settled', 'closed'].includes(c.status));
        const cs = open[0] || null;
        setCaseRow(cs);
        if (!raw.billed_to && cs?.funding?.current?.kind) setKind(cs.funding.current.kind);
      })
      .catch(() => setCaseRow(null))
      .finally(() => setLoading(false));
  }, [currentOrg?.id, clientId]); // eslint-disable-line

  if (!clientId) return null;

  const kinds = caseRow ? [...new Set((caseRow.coverages || []).map((c) => c.kind))] : [];
  const covFor = (k) => (caseRow?.coverages || []).find((c) => c.kind === k);
  const chip = caseRow?.funding?.current?.chip;

  const save = async () => {
    setSaving(true); setError('');
    try {
      const body = { billed_amount: amount === '' ? null : Number(amount), billed_to: kind || null, billed_coverage_id: covFor(kind)?.id || null };
      const updated = await api.updateAppointment(currentOrg.id, appointment.id, body);
      setSaved(amount !== '');
      onSaved?.(updated);
      // Re-read the case so the remaining figure reflects this charge.
      const list = await api.listCases(currentOrg.id).catch(() => null);
      if (list) setCaseRow(list.find((c) => c.id === caseRow?.id) || caseRow);
    } catch (e) {
      setError(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h4 className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-2.5">Visit billing</h4>
      <div className="bg-tertiary border border-theme rounded-xl p-4">
        {loading ? (
          <p className="text-[12px] text-muted-c flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Reading the case's coverage…</p>
        ) : (
          <>
            <div className="text-[12px] text-secondary-c mb-3">
              {caseRow ? (
                <>Case {caseRow.case_number} · bills to <strong className="text-primary-c">{chip || 'no payer on file'}</strong></>
              ) : (
                <>No case on file for this patient — the amount is recorded on the visit only.</>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1 bg-elevated border border-theme rounded-[10px] px-2.5 h-[38px]">
                <DollarSign size={13} className="text-muted-c" />
                <input type="number" min="0" step="0.01" value={amount} onChange={(e) => { setAmount(e.target.value); setSaved(false); }} placeholder={defaultCharge != null ? String(defaultCharge) : 'Amount'} className="w-[96px] bg-transparent text-[13px] text-primary-c focus:outline-none" />
              </div>
              <select value={kind} onChange={(e) => { setKind(e.target.value); setSaved(false); }} className={inputClass}>
                <option value="">{caseRow ? 'Payer…' : 'Self-pay'}</option>
                {(kinds.length ? kinds : Object.keys(KIND_LABEL)).map((k) => {
                  const cov = covFor(k);
                  const rem = cov?.limit_amount != null ? ` · ${money(Math.max(0, Number(cov.limit_amount) - Number(cov.used_amount || 0)))} left` : '';
                  return <option key={k} value={k}>{KIND_LABEL[k]}{cov?.carrier ? ` — ${cov.carrier}` : ''}{rem}</option>;
                })}
              </select>
              <button onClick={save} disabled={saving || (amount === '' && !raw.billed_at)} className="px-3.5 h-[38px] rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50 inline-flex items-center gap-1.5">
                {saving ? <Loader2 size={12} className="animate-spin" /> : saved ? <Check size={12} /> : null} {saved ? 'Recorded' : 'Record charge'}
              </button>
            </div>
            {raw.billed_at && saved && (
              <div className="text-[11px] text-muted-c mt-2">Billed {money(raw.billed_amount ?? amount)} to {KIND_LABEL[raw.billed_to] || raw.billed_to || 'self-pay'} · {new Date(raw.billed_at).toLocaleDateString()}</div>
            )}
            {error && <div className="text-[11.5px] text-danger mt-2">{error}</div>}
            <p className="text-[10.5px] text-muted-c mt-2">The law firm sees the updated balance immediately. Upload the itemized bill or EOB to the referral when you have it.</p>
          </>
        )}
      </div>
    </div>
  );
}
